import React, { useEffect, useState, useContext } from "react";
import {
  makeStyles,
  Typography,
  Box,
  Button,
  Tooltip,
  Chip,
  Fade,
  Badge,
} from "@material-ui/core";
import {
  AccountBalanceWallet as WalletIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { useRouter } from "./../util/router";
import { useAuth } from "./../util/auth";
import { db } from "../util/firebase";
import { doc, onSnapshot } from "firebase/firestore";
// Removed FormContext import
import { useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    zIndex: 1001,
  },
  creditsBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(30, 30, 30, 0.95)"
        : "rgba(245, 245, 245, 0.95)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.05)"
    }`,
  },
  creditsContainer: {
    display: "flex",
    flexDirection: "column",
  },
  creditsInfo: {
    display: "flex",
    alignItems: "center",
  },
  creditsText: {
    fontWeight: 600,
    fontSize: "0.85rem",
    display: "flex",
    alignItems: "center",
  },
  creditsChip: {
    marginLeft: theme.spacing(1),
    height: 24,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  estimationText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  upgradeButton: {
    borderRadius: 16,
    padding: theme.spacing(0.5, 2),
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    minWidth: "auto",
    alignSelf: "center",
  },
  infoIcon: {
    fontSize: "0.8rem",
    marginRight: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  toolkitHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  creditsTooltip: {
    maxWidth: 250,
    fontSize: "0.8rem",
  },
  creditsBadge: {
    marginLeft: theme.spacing(0.5),
  },
  creditsBadgeContent: {
    fontSize: "0.7rem",
    height: 18,
    minWidth: 18,
  },
  warningText: {
    color: theme.palette.warning.main,
    fontWeight: 500,
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
}));

const ContextualCreditsDisplay = ({
  showInToolkit = false,
  toolkitTitle = null,
  selectedCount = 0,
  totalSelections = 0,
  showUpgradePrompt = false,
  className,
  formData, // Add formData as a prop
}) => {
  const classes = useStyles();
  const router = useRouter();
  const auth = useAuth();
  // Removed useContext(FormContext)

  const [userData, setUserData] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  // Fetch user data from Firestore
  useEffect(() => {
    if (!auth.user) {
      setUserData(null);
      return;
    }
    const userDocRef = doc(db, "users", auth.user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      setUserData(docSnapshot.exists() ? docSnapshot.data() : null);
    });
    return unsubscribe;
  }, [auth.user]);

  // Extract user data
  const { credits: userCredits = 0, planId = "Free" } = userData || {};

  // Use the passed totalSelections prop directly.
  // The parent component (JobDetailsForm) is responsible for calculating this safely.
  const totalToolkitSelections = totalSelections;

  // Calculate estimated remaining credits
  const estimatedCreditsRemaining = Math.max(
    userCredits - totalToolkitSelections,
    0
  );

  // Determine credit status for styling
  const creditStatus =
    estimatedCreditsRemaining === 0
      ? "empty"
      : estimatedCreditsRemaining < 5
      ? "low"
      : "normal";

  // Get badge color based on credit status
  const getBadgeColor = () => {
    switch (creditStatus) {
      case "empty":
        return "error";
      case "low":
        return "secondary";
      default:
        return "primary";
    }
  };

  // Compact toolkit header display
  if (showInToolkit) {
    return (
      <Box className={classes.toolkitHeader}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" component="div">
            {toolkitTitle || "Job Application Toolkit"}
          </Typography>
          <Tooltip
            title={
              <span className={classes.creditsTooltip}>
                You have {userCredits} total credits. Estimated{" "}
                {estimatedCreditsRemaining} credits will remain after generating
                your selections.
              </span>
            }
            arrow
          >
            <Badge
              badgeContent={estimatedCreditsRemaining}
              color={getBadgeColor()}
              overlap="rectangular" // Corrected value
              className={classes.creditsBadge}
              classes={{
                badge: classes.creditsBadgeContent,
              }}
            >
              <WalletIcon fontSize="small" />
            </Badge>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center">
          <Typography variant="body2" color="textSecondary" component="span">
            <InfoIcon className={classes.infoIcon} />
            {selectedCount} option{selectedCount !== 1 ? "s" : ""} selected
          </Typography>
        </Box>
      </Box>
    );
  }

  // Main credits display
  return (
    <Fade in={isVisible}>
      <div className={`${classes.root} ${className || ""}`}>
        <Box className={classes.creditsBar}>
          {!auth.user ? (
            // Display for non-authenticated users
            <div className={classes.creditsContainer}>
              <div className={classes.creditsInfo}>
                <Typography className={classes.creditsText} component="div">
                  <WalletIcon fontSize="small" style={{ marginRight: 4 }} />
                  Sign in to access your credits
                </Typography>
              </div>
            </div>
          ) : (
            // Display for authenticated users
            <>
              <div className={classes.creditsContainer}>
                <div className={classes.creditsInfo}>
                  <Typography className={classes.creditsText} component="div">
                    <WalletIcon fontSize="small" style={{ marginRight: 4 }} />
                    {planId.charAt(0).toUpperCase() + planId.slice(1)} Plan:
                    <Chip
                      label={`${userCredits} Credits`}
                      size="small"
                      className={classes.creditsChip}
                      color="primary"
                    />
                  </Typography>
                </div>

                <Typography className={classes.estimationText} component="div">
                  {creditStatus === "empty" ? (
                    <span className={classes.errorText}>
                      No credits will remain after generating your selections.
                    </span>
                  ) : creditStatus === "low" ? (
                    <span className={classes.warningText}>
                      Est. {estimatedCreditsRemaining} credits will remain after
                      generating your selections.
                    </span>
                  ) : (
                    <span>
                      Est. {estimatedCreditsRemaining} credits will remain after
                      generating your selections.
                    </span>
                  )}
                </Typography>
              </div>

              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.upgradeButton}
                onClick={() => router.push("/pricing")}
                style={{
                  display:
                    creditStatus === "empty" || creditStatus === "low"
                      ? "block"
                      : "none",
                }}
              >
                Upgrade
              </Button>
            </>
          )}
        </Box>
      </div>
    </Fade>
  );
};

export default ContextualCreditsDisplay;
